import { useMemo, useState } from 'react';
import type { OrgTreeNode } from '@vtak/shared';
import { LogOut, ShieldCheck, Search, Building2, ArrowRight, ChevronRight } from 'lucide-react';
import { useOrgTreesQuery } from '@/queries/use-org-trees-query';
import { flattenTree, type FlatOrgUnit } from '@/lib/org-tree';
import { switchWorkspace } from '@/lib/workspace-switch';
import { useAuth } from '@/auth/AuthProvider';
import { cn } from '@/lib/utils';
import vtakLogo from '@/assets/vtak-logo.png';

/**
 * Full-screen unit browser shown to a platform-admin (Velocity super admin) who
 * holds no unit membership. Instead of the join-code wall, they enter the whole
 * platform in one click or drill into any unit through a collapsible org tree —
 * the server authorizes the manifest via the platform-admin bypass (a synthetic,
 * non-persisted admin preview). Selecting a unit reloads into that workspace.
 */
export function PlatformAdminPicker(): React.JSX.Element {
  const { logout } = useAuth();
  const trees = useOrgTreesQuery(true);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const term = filter.trim();
  const orgs = trees.data ?? [];

  // Flat, filtered matches — shown while searching so nested units surface
  // regardless of which branches are collapsed.
  const matches = useMemo(
    () =>
      orgs.map((org) => ({
        orgId: org.orgId,
        orgName: org.orgName,
        units: org.tree
          .flatMap((root) => flattenTree(root, 0))
          .filter(({ node }) => matchesFilter(node.name, node.shortName, term)),
      })),
    [orgs, term],
  );
  const hasMatches = matches.some((org) => org.units.length > 0);

  // The top of the org tree — the one-click "Velocity Admin" entry for the system
  // owner. Enters the root unit, from which a platform-admin sees everything below.
  const rootUnitId = orgs.find((org) => org.tree.length > 0)?.tree[0]?.id ?? null;

  const toggle = (id: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="fixed inset-0 z-[2000] grid place-items-center bg-[#0d0d0d] p-4">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-xl border border-echo-border bg-[#1a1a1a] p-6 shadow-2xl">
        <div className="mb-4 flex flex-col items-center gap-3">
          <img src={vtakLogo} alt="VTAK" className="h-11 w-auto" />
          <span className="flex items-center gap-1.5 rounded bg-echo-yellow/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-echo-yellow">
            <ShieldCheck className="h-3 w-3" />
            Platform admin
          </span>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Enter the platform</p>
        </div>

        {rootUnitId && (
          <button
            type="button"
            onClick={() => switchWorkspace(rootUnitId)}
            className="group mb-4 flex w-full items-center gap-3 rounded-lg border border-echo-yellow/40 bg-echo-yellow/10 px-3.5 py-3 text-left transition-colors hover:bg-echo-yellow/15 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-echo-yellow"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-echo-yellow/20 text-echo-yellow">
              <Building2 className="h-[18px] w-[18px]" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-foreground">Enter as Velocity Admin</span>
              <span className="block text-[11px] text-muted-foreground">Full oversight</span>
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-echo-yellow/70 transition-transform group-hover:translate-x-0.5" />
          </button>
        )}

        <p className="mb-2 text-center text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60">
          or enter a specific unit
        </p>

        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter units…"
            className="w-full rounded-md border border-echo-border bg-[#111] py-2 pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-echo-yellow/40 focus-visible:outline-none"
          />
        </div>

        <div className="-mx-1 flex-1 overflow-y-auto px-1">
          {trees.isLoading && <p className="px-2 py-2 text-xs text-muted-foreground">Loading organizations…</p>}

          {/* Searching → flat matches; browsing → collapsible tree. */}
          {!trees.isLoading && term.length > 0 && !hasMatches && (
            <p className="px-2 py-2 text-xs text-muted-foreground">No units match “{term}”.</p>
          )}

          {term.length > 0
            ? matches.map((org) =>
                org.units.length === 0 ? null : (
                  <OrgSection key={org.orgId} name={org.orgName}>
                    {org.units.map((unit) => (
                      <FlatUnitRow key={unit.node.id} unit={unit} />
                    ))}
                  </OrgSection>
                ),
              )
            : orgs.map((org) =>
                org.tree.length === 0 ? null : (
                  <OrgSection key={org.orgId} name={org.orgName}>
                    {org.tree.map((root) => (
                      <TreeNode
                        key={root.id}
                        node={root}
                        depth={0}
                        expanded={expanded}
                        onToggle={toggle}
                      />
                    ))}
                  </OrgSection>
                ),
              )}
        </div>

        <button
          onClick={logout}
          className="mt-4 inline-flex items-center justify-center gap-1.5 self-center text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign out
        </button>
      </div>
    </div>
  );
}

/** An organization heading wrapping its unit rows. */
function OrgSection({ name, children }: { name: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <section className="mb-3">
      <p className="mb-1 px-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{name}</p>
      <div className="flex flex-col gap-0.5">{children}</div>
    </section>
  );
}

/** A collapsible org-tree node: a chevron toggles children, the label enters the unit. */
function TreeNode({
  node,
  depth,
  expanded,
  onToggle,
}: {
  node: OrgTreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
}): React.JSX.Element {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  return (
    <>
      <div
        className="flex items-center rounded-md transition-colors hover:bg-white/[0.06]"
        style={{ paddingLeft: 4 + depth * 14 }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            aria-label={isOpen ? `Collapse ${node.shortName}` : `Expand ${node.shortName}`}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none"
          >
            <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-90')} />
          </button>
        ) : (
          <span className="w-6 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => switchWorkspace(node.id)}
          className="flex min-w-0 flex-1 items-center gap-2 py-1.5 pr-2 text-left"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-medium text-foreground">{node.shortName}</span>
            <span className="block truncate text-[10px] text-muted-foreground/80">{node.name}</span>
          </span>
          <span className="shrink-0 font-mono text-[9px] uppercase tracking-wide text-muted-foreground/60">
            {node.echelon}
          </span>
        </button>
      </div>
      {hasChildren &&
        isOpen &&
        node.children.map((child) => (
          <TreeNode key={child.id} node={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} />
        ))}
    </>
  );
}

/** A flat, indented unit row used in filtered results; click enters the unit. */
function FlatUnitRow({ unit }: { unit: FlatOrgUnit }): React.JSX.Element {
  const { node, depth } = unit;
  return (
    <button
      type="button"
      onClick={() => switchWorkspace(node.id)}
      style={{ paddingLeft: 8 + depth * 14 }}
      className="flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left transition-colors hover:bg-white/[0.06]"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-foreground">{node.shortName}</p>
        <p className="truncate text-[10px] text-muted-foreground/80">{node.name}</p>
      </div>
      <span className="shrink-0 font-mono text-[9px] uppercase tracking-wide text-muted-foreground/60">
        {node.echelon}
      </span>
    </button>
  );
}

/** Case-insensitive substring match over a unit's name and short name. */
function matchesFilter(name: string, shortName: string, term: string): boolean {
  const q = term.toLowerCase();
  if (!q) return true;
  return name.toLowerCase().includes(q) || shortName.toLowerCase().includes(q);
}
